import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card, PageHeader, Button, Modal, Input, Select, Spinner, EmptyState } from "@/components/common";
import { Plus, Pencil, Trash2, Dumbbell, Clock, MapPin, Users } from "lucide-react";

const GIORNI = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
const EMPTY = { nome: "", disciplina: "", istruttore_id: "", assistente_id: "", giorni: [], orario: "", impianto: "", quota: 0, partecipanti: [] };

export default function Corsi() {
  const { can } = useAuth();
  const editable = can("corsi", "edit");
  const [items, setItems] = useState(null);
  const [persone, setPersone] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => {
    api.get("/corsi").then((r) => setItems(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/persone").then((r) => setPersone(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (key, id) => setForm((f) => ({ ...f, [key]: f[key].includes(id) ? f[key].filter((x) => x !== id) : [...f[key], id] }));
  const save = async () => {
    if (!form.nome) { toast.error("Nome corso obbligatorio"); return; }
    try { if (editId) await api.put(`/corsi/${editId}`, { ...form, quota: Number(form.quota) }); else await api.post("/corsi", { ...form, quota: Number(form.quota) }); toast.success("Salvato"); setOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/corsi/${id}`); load(); };

  if (!items) return <Spinner />;
  const atleti = persone.filter((p) => (p.ruoli || []).includes("atleta"));
  const istruttori = persone.filter((p) => (p.ruoli || []).includes("istruttore"));

  return (
    <div>
      <PageHeader title="Corsi e Squadre" subtitle={`${items.length} gruppi attivi`}
        actions={editable && <Button onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} data-testid="add-corso-btn"><Plus size={16} /> Nuovo corso</Button>} />
      {items.length === 0 ? <Card className="p-6"><EmptyState icon={Dumbbell} title="Nessun corso" /></Card> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((c) => (
            <Card key={c.id} className="p-5" data-testid={`corso-card-${c.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div><span className="text-xs font-semibold text-sky-600 uppercase tracking-wider">{c.disciplina}</span><h3 className="font-bold font-display text-lg text-slate-900">{c.nome}</h3></div>
                {editable && <div className="flex gap-1">
                  <button onClick={() => { setForm({ ...EMPTY, ...c }); setEditId(c.id); setOpen(true); }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><Pencil size={14} /></button>
                  <button onClick={() => del(c.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={14} /></button>
                </div>}
              </div>
              <div className="space-y-2 text-sm text-slate-600">
                <p className="flex items-center gap-2"><Users size={15} className="text-slate-400" /> Istruttore: {c.istruttore_name}</p>
                <p className="flex items-center gap-2"><Clock size={15} className="text-slate-400" /> {(c.giorni || []).join(", ") || "—"} · {c.orario || "—"}</p>
                <p className="flex items-center gap-2"><MapPin size={15} className="text-slate-400" /> {c.impianto || "—"}</p>
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                <span className="text-sm text-slate-500"><strong className="text-slate-800">{c.n_partecipanti}</strong> atleti</span>
                <span className="text-sm font-semibold text-emerald-600">€{c.quota}/anno</span>
              </div>
            </Card>
          ))}
        </div>
      )}
      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Modifica corso" : "Nuovo corso"} wide>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Nome (es. Under 15)" data-testid="corso-nome" value={form.nome} onChange={(e) => upd("nome", e.target.value)} />
            <Input label="Disciplina" value={form.disciplina} onChange={(e) => upd("disciplina", e.target.value)} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Select label="Istruttore" value={form.istruttore_id} onChange={(e) => upd("istruttore_id", e.target.value)}><option value="">—</option>{istruttori.map((p) => <option key={p.id} value={p.id}>{p.cognome} {p.nome}</option>)}</Select>
            <Select label="Assistente" value={form.assistente_id} onChange={(e) => upd("assistente_id", e.target.value)}><option value="">—</option>{istruttori.map((p) => <option key={p.id} value={p.id}>{p.cognome} {p.nome}</option>)}</Select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Giorni</label>
            <div className="flex flex-wrap gap-2">{GIORNI.map((g) => <button key={g} type="button" onClick={() => toggle("giorni", g)} className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border ${form.giorni.includes(g) ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-300"}`}>{g.slice(0, 3)}</button>)}</div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <Input label="Orario" value={form.orario} onChange={(e) => upd("orario", e.target.value)} placeholder="17:00-18:30" />
            <Input label="Impianto" value={form.impianto} onChange={(e) => upd("impianto", e.target.value)} />
            <Input label="Quota €/anno" type="number" value={form.quota} onChange={(e) => upd("quota", e.target.value)} />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Partecipanti ({form.partecipanti.length})</label>
            <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-1">{atleti.map((a) => <button key={a.id} type="button" onClick={() => toggle("partecipanti", a.id)} className={`px-2.5 py-1 rounded-lg text-xs border ${form.partecipanti.includes(a.id) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300"}`}>{a.cognome} {a.nome}</button>)}</div>
          </div>
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={save} data-testid="save-corso-btn">Salva</Button></div>
        </div>
      </Modal>
    </div>
  );
}
